import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Globals } from '@aiwyn/config';
import { API_BASE_PATH } from '@aiwyn/api';

/**
 * REST client for the real CRL backend endpoints (deployed to martinfreire) that
 * replace the legacy Hasura reads behind the redesigned Client-Request-List.
 *
 * Two reads back the detail page: `/page` returns the request + its items with a
 * `fileCount` but NO files/answers, and `/item/detail` lazily returns one item's
 * files + answer when the overlay opens. The `/cxp/tasks/*` pair backs the tasks
 * list and its nav badge.
 *
 * Each group has its own flag: the detail reads follow
 * `CXP_ENABLE_REQUEST_LIST_REDESIGN`, the `/cxp/tasks/*` pair follows
 * `CXP_ENABLE_UNIFIED_TASKS_ENDPOINTS`. With a flag off, that group stays on
 * the Hasura reads.
 *
 * All requests hit `globals.artUrl`; the `AuthInterceptor` attaches the auth
 * token automatically, so no token is passed here.
 */
@Injectable({ providedIn: 'root' })
export class CxpCrlRestApiService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  /** `GET /api/secure/client-request/page` — the request + items (no files/answers). */
  getCrlPage(clientRequestId: number): Observable<CrlPageResponse> {
    const params = new HttpParams().set('clientRequestId', clientRequestId);
    return this.http.get<CrlPageResponse>(`${this.globals.artUrl}${CRL_PAGE_PATH}`, { params });
  }

  /** `GET /api/secure/client-request/item/detail` — one item's files + answer. */
  getCrlItemDetail(clientRequestId: number, clientRequestItemId: number): Observable<CrlItemDetailResponse> {
    const params = new HttpParams().set('clientRequestId', clientRequestId).set('clientRequestItemId', clientRequestItemId);
    return this.http.get<CrlItemDetailResponse>(`${this.globals.artUrl}${CRL_ITEM_DETAIL_PATH}`, { params });
  }

  /** `GET /api/secure/cxp/tasks/count` — open/total counts for the nav badge. */
  getTaskCount(): Observable<CrlTaskCountResponse> {
    return this.http.get<CrlTaskCountResponse>(`${this.globals.artUrl}${CXP_TASKS_COUNT_PATH}`);
  }

  /** `GET /api/secure/cxp/tasks/list` — CRL + deliverable rows, pre-joined and scoped. */
  getTaskList(): Observable<CrlTaskListResponse> {
    return this.http.get<CrlTaskListResponse>(`${this.globals.artUrl}${CXP_TASKS_LIST_PATH}`);
  }
}

const CRL_PAGE_PATH = `${API_BASE_PATH}/client-request/page`;
const CRL_ITEM_DETAIL_PATH = `${API_BASE_PATH}/client-request/item/detail`;
const CXP_TASKS_COUNT_PATH = `${API_BASE_PATH}/cxp/tasks/count`;
const CXP_TASKS_LIST_PATH = `${API_BASE_PATH}/cxp/tasks/list`;

// --- raw response contracts --------------------------------------------------
//
// These mirror the backend JSON exactly (the bridge maps them into the CXP-local
// `CrlRequestDetail` model). Kept as the isolation seam so the FE models never
// leak the wire shape.

/** Request-level status from `/page`. */
export type CrlPageStatus = 'DRAFT' | 'SENT' | 'IN_PROGRESS' | 'IN_REVIEW' | 'DONE';

/** Raw per-item status — the SAME enum values as `ClientRequestItemStatus`. */
export type CrlRawItemStatus = 'TO_DO' | 'OPEN' | 'IN_REVIEW' | 'REJECTED' | 'DONE';

/** Raw item type — the SAME enum values as `RequestItemType`. */
export type CrlRawItemType = 'DOCUMENT_REQUEST' | 'E_SIGN' | 'QUESTIONNAIRE';

export interface CrlServiceLineRef {
  id: number;
  name: string;
}

export interface CrlReviewerRef {
  staffId: number;
  name: string;
  email?: string;
  isOwner: boolean;
}

export interface CrlProgressCounts {
  completed: number;
  total: number;
}

export interface CrlItemStatusCounts {
  open: number;
  inReview: number;
  rejected: number;
  done: number;
  total: number;
}

export interface CrlIntegrationRef {
  type: string;
  url: string;
}

/** CONTACT = a client contact assignee; SECURE_LINK = an invited guest with item-scoped access. */
export type CrlApiAssigneeType = 'CONTACT' | 'SECURE_LINK';

/** `id` is the secure_link primary key — the handle the revoke endpoint's `secureLinkIds` expects. */
export interface CrlApiSecureLinkRef {
  id: number;
  createdAt?: string;
  expireAt?: string;
}

export interface CrlPageAssignee {
  type?: CrlApiAssigneeType;
  contactId?: number | null;
  fullName: string;
  email?: string;
  /** True for a contact assigned at the request level — the client owner. Never true for a secure-link guest. */
  isOwner?: boolean;
  secureLink?: CrlApiSecureLinkRef | null;
}

export interface CrlPageItemAssignee {
  type?: CrlApiAssigneeType;
  id?: number | null;
  contactId?: number | null;
  fullName?: string;
  email?: string;
  /** True for the request-level assignee (client owner) where they also hold this item. */
  isOwner?: boolean;
  secureLink?: CrlApiSecureLinkRef | null;
}

export interface CrlPageItem {
  id: number;
  name: string;
  description?: string;
  ordinal: number;
  type: CrlRawItemType;
  status: CrlRawItemStatus;
  dueDate?: string;
  isReadyToSubmit: boolean;
  isNotApplicable: boolean;
  notApplicableReason?: string;
  flagMessage?: string;
  restricted: boolean;
  isContentMasked: boolean;
  assignees: CrlPageItemAssignee[];
  fileCount: number;
}

export interface CrlPageSection {
  id: number;
  name: string;
  ordinal: number;
  items: CrlPageItem[];
}

export interface CrlPageResponse {
  clientRequestId: number;
  name: string;
  description?: string;
  status: CrlPageStatus;
  jobId?: number;
  clientId: number;
  clientName?: string;
  serviceLine?: CrlServiceLineRef;
  reviewers: CrlReviewerRef[];
  progress: CrlProgressCounts;
  itemStatusCounts: CrlItemStatusCounts;
  nextDueDate?: string;
  overdueCount: number;
  rejectedItemCount: number;
  hasRestrictedItems: boolean;
  isFieldGuide: boolean;
  integration?: CrlIntegrationRef;
  submittedAt?: string;
  updatedAt?: string;
  completedAt?: string;
  assignees: CrlPageAssignee[];
  sections: CrlPageSection[];
}

export interface CrlItemDetailFile {
  id?: number;
  fileId?: number;
  isFlagged: boolean;
  createdAt?: string;
  name?: string;
  dmsFileId?: string;
  dmsId?: string;
  size?: number;
  isMasked: boolean;
}

export interface CrlItemDetailFileReference {
  id?: number;
  fileId?: number;
  createdAt?: string;
  active: boolean;
  name?: string;
  dmsId?: string;
  size?: number;
}

export interface CrlItemDetailResponse {
  clientRequestId: number;
  clientRequestItemId: number;
  name: string;
  description?: string;
  ordinal: number;
  type: CrlRawItemType;
  status: CrlRawItemStatus;
  dueDate?: string;
  isReadyToSubmit: boolean;
  isNotApplicable: boolean;
  notApplicableReason?: string;
  flagMessage?: string;
  restricted: boolean;
  isContentMasked: boolean;
  reviewers: CrlReviewerRef[];
  assignees: CrlPageItemAssignee[];
  /** JSON answer payload; null when the content is masked for this caller. */
  answer?: { answer?: string } | null;
  files: CrlItemDetailFile[];
  fileReferences: CrlItemDetailFileReference[];
}

// --- tasks --------------------------------------------------------------------

export interface CrlTaskCountBreakdown {
  source: string;
  open: number;
  total: number;
}

export interface CrlTaskCountResponse {
  open: number;
  total: number;
  breakdown: CrlTaskCountBreakdown[];
}

export interface CrlTaskIntegration {
  type: string;
  url: string;
  signerGuid?: string;
  signerName?: string;
  signerLabel?: string;
  isSigned?: boolean;
}

export interface CrlTaskRow {
  key: string;
  id: number;
  sourceId: number;
  type: string;
  name: string;
  description?: string;
  createdAt?: string;
  dueDate?: string;
  updatedAt?: string;
  lastViewedAt?: string;
  status: 'OPEN' | 'COMPLETED';
  sourceStatus: string;
  actionRequired: boolean;
  progress?: CrlProgressCounts;
  clientId?: number;
  clientName?: string;
  jobId?: number;
  serviceLine?: CrlServiceLineRef;
  staffIds: number[];
  integration?: CrlTaskIntegration;
}

export interface CrlTaskListResponse {
  items: CrlTaskRow[];
  openCount: number;
  completedCount: number;
  totalCount: number;
}
